<template>
  <v-card flat :dark="isDarkMode" :light="!isDarkMode" class="employee-planning-hover" :class="{ 'employee-planning-hover--light': !isDarkMode }">
    <div class="employee-planning-hover__accent" :class="hoursStateClass"></div>

    <header class="employee-planning-hover__header">
      <div class="employee-planning-hover__identity">
        <v-avatar size="48" class="employee-planning-hover__avatar">
          <img v-if="showPicture" :src="picture" :alt="displayName" @error="onImageError" />
          <span v-else class="employee-planning-hover__initials">{{ initials }}</span>
        </v-avatar>

        <div class="employee-planning-hover__person-copy">
          <span class="employee-planning-hover__eyebrow">Planungsübersicht</span>
          <strong class="employee-planning-hover__name">{{ displayName }}</strong>
          <span class="employee-planning-hover__contract">
            <v-icon x-small>mdi-chart-donut</v-icon>
            {{ percentageLabel }} Pensum
          </span>
        </div>
      </div>

      <span class="employee-planning-hover__state" :class="hoursStateClass">
        <span class="employee-planning-hover__state-dot"></span>
        {{ hoursStateLabel }}
      </span>
    </header>

    <section class="employee-planning-hover__hours">
      <div class="employee-planning-hover__hours-main">
        <div>
          <span class="employee-planning-hover__section-label">Monatspensum</span>
          <div class="employee-planning-hover__hours-value">
            {{ formatHours(plannedHours) }}
            <small>/ {{ formatHours(targetHours) }}</small>
          </div>
        </div>

        <div class="employee-planning-hover__balance" :class="hoursStateClass">
          <span>{{ signedHours(balanceHours) }}</span>
          <small>Bilanz</small>
        </div>
      </div>

      <div class="employee-planning-hover__progress-track" aria-hidden="true">
        <span class="employee-planning-hover__progress-fill" :class="hoursStateClass" :style="{ width: progressWidth }"></span>
        <span class="employee-planning-hover__target-marker"></span>
      </div>

      <div class="employee-planning-hover__progress-caption">
        <span>{{ progressLabel }} erfüllt</span>
        <span>{{ remainingHoursLabel }}</span>
      </div>
    </section>

    <section class="employee-planning-hover__metrics">
      <div class="employee-planning-hover__metric">
        <span class="employee-planning-hover__metric-icon employee-planning-hover__metric-icon--blue">
          <v-icon small>mdi-calendar-week</v-icon>
        </span>
        <div>
          <strong>{{ numberValue(overview.weekShiftCount) }} Schichten</strong>
          <span>Diese Woche · {{ formatHours(overview.weekHours) }}</span>
        </div>
      </div>

      <div class="employee-planning-hover__metric">
        <span class="employee-planning-hover__metric-icon employee-planning-hover__metric-icon--violet">
          <v-icon small>mdi-calendar-arrow-left</v-icon>
        </span>
        <div>
          <strong>{{ previousWeekendTitle }}</strong>
          <span>{{ previousWeekendSubtitle }}</span>
        </div>
      </div>

      <div class="employee-planning-hover__metric">
        <span class="employee-planning-hover__metric-icon employee-planning-hover__metric-icon--amber">
          <v-icon small>mdi-weather-sunset</v-icon>
        </span>
        <div>
          <strong>{{ numberValue(overview.monthWeekendShiftCount) }} WE-Schichten</strong>
          <span>Im aktuellen Monat</span>
        </div>
      </div>

      <div class="employee-planning-hover__metric employee-planning-hover__metric--distribution">
        <span class="employee-planning-hover__metric-icon employee-planning-hover__metric-icon--green">
          <v-icon small>mdi-chart-timeline-variant</v-icon>
        </span>
        <div>
          <strong>Schichtmix</strong>
          <span class="employee-planning-hover__mix">
            <b>F {{ shiftStat("morning") }}</b>
            <b>Z {{ shiftStat("intermediate") }}</b>
            <b>S {{ shiftStat("late") }}</b>
          </span>
        </div>
      </div>
    </section>

    <section class="employee-planning-hover__week">
      <div class="employee-planning-hover__week-title">
        <span>
          <v-icon x-small>mdi-calendar-range</v-icon>
          Wochenblick
        </span>
        <small>{{ scheduledDaysCount }}/{{ sourceDaysCount }} Tage geplant</small>
      </div>

      <div class="employee-planning-hover__days" :style="{ gridTemplateColumns: `repeat(${normalizedDays.length}, minmax(0, 1fr))` }">
        <v-tooltip v-for="(day, index) in normalizedDays" :key="dayKey(day, index)" top max-width="250">
          <template v-slot:activator="{ on, attrs }">
            <div class="employee-planning-hover__day" :class="dayStateClass(day)" v-bind="attrs" v-on="on">
              <span class="employee-planning-hover__day-label">{{ day.shortLabel }}</span>
              <strong>{{ dayDateNumber(day) }}</strong>
              <span class="employee-planning-hover__day-hours">{{ dayHours(day) }}</span>
              <span class="employee-planning-hover__day-dot"></span>
            </div>
          </template>

          <div class="employee-planning-hover__tooltip">
            <strong>{{ day.label || day.shortLabel }}</strong>
            <span>{{ day.stateLabel || stateLabel(day.state) }}</span>
            <span v-if="numberValue(day.shiftCount)">
              {{ numberValue(day.shiftCount) }} {{ numberValue(day.shiftCount) === 1 ? "Schicht" : "Schichten" }} · {{ formatHours(day.hours) }}
            </span>
            <span v-for="(status, statusIndex) in normalizedStatuses(day)" :key="statusIndex">{{ status }}</span>
          </div>
        </v-tooltip>
      </div>
    </section>
  </v-card>
</template>

<script>
export default {
  name: "ShiftManagementEmployeePlanningHover",
  props: {
    overview: {
      type: Object,
      required: true,
    },
    picture: {
      type: String,
      default: "",
    },
    isDarkMode: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      imageFailed: false,
    }
  },
  computed: {
    displayName() {
      return this.overview && this.overview.name ? this.overview.name : "Mitarbeiter"
    },
    showPicture() {
      return Boolean(this.picture) && !this.imageFailed
    },
    initials() {
      const words = this.displayName.trim().split(/\s+/).filter(Boolean)
      if (!words.length) return "MA"
      return words
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join("")
    },
    percentageLabel() {
      const percentage = Number(this.overview && this.overview.percentage)
      return `${Number.isFinite(percentage) ? Math.round(percentage) : 0}%`
    },
    targetHours() {
      return this.numberValue(this.overview && this.overview.targetHours)
    },
    plannedHours() {
      return this.numberValue(this.overview && this.overview.plannedHours)
    },
    balanceHours() {
      const supplied = Number(this.overview && this.overview.balanceHours)
      return Number.isFinite(supplied) ? supplied : this.plannedHours - this.targetHours
    },
    progressValue() {
      const supplied = Number(this.overview && this.overview.progress)
      if (Number.isFinite(supplied)) return Math.max(0, supplied)
      if (!this.targetHours) return this.plannedHours > 0 ? 100 : 0
      return Math.max(0, (this.plannedHours / this.targetHours) * 100)
    },
    progressWidth() {
      return `${Math.min(this.progressValue, 100)}%`
    },
    progressLabel() {
      return `${Math.round(this.progressValue)}%`
    },
    remainingHoursLabel() {
      if (this.balanceHours < -0.05) return `${this.formatHours(Math.abs(this.balanceHours))} offen`
      if (this.balanceHours > 0.05) return `${this.formatHours(this.balanceHours)} über Soll`
      return "Soll erreicht"
    },
    normalizedHoursState() {
      const state = String((this.overview && this.overview.hoursState) || "").toLowerCase()
      if (["neutral", "none", "no-target"].includes(state)) return "neutral"
      if (["over", "overtime", "above", "high", "danger"].includes(state)) return "over"
      if (["ok", "balanced", "done", "target", "success"].includes(state)) return "balanced"
      if (["warning", "near", "close"].includes(state)) return "near"
      if (this.balanceHours > 0.05) return "over"
      if (Math.abs(this.balanceHours) <= 0.05) return "balanced"
      return "under"
    },
    hoursStateClass() {
      return `employee-planning-hover--${this.normalizedHoursState}`
    },
    hoursStateLabel() {
      if (this.overview && this.overview.hoursStateLabel) return this.overview.hoursStateLabel
      const labels = {
        neutral: "Kein Studio-Soll",
        under: "Kapazität frei",
        near: "Nahe am Soll",
        balanced: "Soll erreicht",
        over: "Über Soll",
      }
      return labels[this.normalizedHoursState]
    },
    previousWeekendTitle() {
      const count = this.numberValue(this.overview && this.overview.previousWeekendShiftCount)
      return count ? `${count} ${count === 1 ? "Schicht" : "Schichten"}` : "Frei"
    },
    previousWeekendSubtitle() {
      return (this.overview && this.overview.previousWeekendLabel) || "Letztes Wochenende"
    },
    normalizedDays() {
      const days = Array.isArray(this.overview && this.overview.days) ? this.overview.days.slice(0, 7) : []
      const fallbackLabels = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"]
      if (!days.length) {
        fallbackLabels.forEach((label) =>
          days.push({
            shortLabel: label,
            label,
            state: "empty",
            stateLabel: "Keine Angaben",
            shiftCount: 0,
            hours: 0,
            statuses: [],
          }),
        )
      }
      return days.map((day, index) => ({
        ...day,
        shortLabel: day.shortLabel || fallbackLabels[index],
      }))
    },
    sourceDaysCount() {
      const count = Array.isArray(this.overview && this.overview.days) ? this.overview.days.length : 0
      return count || 7
    },
    scheduledDaysCount() {
      return this.normalizedDays
        .slice(0, this.sourceDaysCount)
        .filter((day) => this.numberValue(day.shiftCount) > 0 || this.numberValue(day.hours) > 0).length
    },
  },
  watch: {
    picture() {
      this.imageFailed = false
    },
  },
  methods: {
    onImageError() {
      this.imageFailed = true
    },
    numberValue(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },
    formatHours(value) {
      const hours = this.numberValue(value)
      const digits = Math.abs(hours % 1) > 0.04 ? 1 : 0
      return `${hours.toLocaleString("de-CH", { minimumFractionDigits: digits, maximumFractionDigits: 1 })}h`
    },
    signedHours(value) {
      const hours = this.numberValue(value)
      const prefix = hours > 0.05 ? "+" : ""
      return `${prefix}${this.formatHours(hours)}`
    },
    shiftStat(type) {
      return this.numberValue(this.overview && this.overview.stats && this.overview.stats[type])
    },
    dayKey(day, index) {
      return (day && day.date) || `${day.shortLabel}-${index}`
    },
    dayDateNumber(day) {
      if (!day || !day.date) return "–"
      const value = String(day.date)
      const isoMatch = value.match(/^\d{4}-\d{1,2}-(\d{1,2})/)
      if (isoMatch) return String(Number(isoMatch[1]))
      const localMatch = value.match(/^(\d{1,2})[./-]\d{1,2}[./-]\d{4}/)
      if (localMatch) return String(Number(localMatch[1]))
      const parsed = new Date(day.date)
      return Number.isNaN(parsed.getTime()) ? "–" : parsed.getDate()
    },
    dayHours(day) {
      const hours = this.numberValue(day && day.hours)
      if (!hours) return this.numberValue(day && day.shiftCount) ? `${this.numberValue(day.shiftCount)}×` : "frei"
      return this.formatHours(hours)
    },
    normalizedDayState(day) {
      const state = String((day && day.state) || "empty").toLowerCase()
      if (["planned", "shift", "assigned", "working", "work"].includes(state)) return "planned"
      if (["external", "otherstudio", "other-studio", "away"].includes(state)) return "external"
      if (["vacation", "holiday", "ferien"].includes(state)) return "vacation"
      if (["school", "schultag"].includes(state)) return "school"
      if (["wish", "freewish", "free-wish", "freiwunsch"].includes(state)) return "wish"
      if (["sick", "absence", "absent", "krank"].includes(state)) return "absence"
      if (["blocked", "conflict", "warning", "error"].includes(state)) return "conflict"
      if (["off", "free", "frei"].includes(state)) return "free"
      return "empty"
    },
    dayStateClass(day) {
      return `employee-planning-hover__day--${this.normalizedDayState(day)}`
    },
    stateLabel(state) {
      const labels = {
        planned: "Schicht geplant",
        external: "Arbeitet in anderem Studio",
        vacation: "Ferien",
        school: "Schultag",
        wish: "Freiwunsch",
        absence: "Abwesend",
        conflict: "Konflikt prüfen",
        free: "Frei",
        empty: "Noch nicht geplant",
      }
      return labels[this.normalizedDayState({ state })]
    },
    normalizedStatuses(day) {
      if (!day || !Array.isArray(day.statuses)) return []
      return day.statuses
        .map((status) => {
          if (typeof status === "string") return status
          if (!status || typeof status !== "object") return ""
          return status.label || status.name || status.title || status.text || ""
        })
        .filter(Boolean)
    },
  },
}
</script>

<style scoped>
.employee-planning-hover {
  --eph-bg: #181b20;
  --eph-panel: rgba(255, 255, 255, 0.045);
  --eph-panel-strong: rgba(255, 255, 255, 0.075);
  --eph-border: rgba(255, 255, 255, 0.1);
  --eph-text: #f5f7fa;
  --eph-muted: #9ca5b4;
  --eph-track: rgba(255, 255, 255, 0.09);
  position: relative;
  width: 400px;
  max-width: calc(100vw - 24px);
  overflow: hidden;
  border: 1px solid var(--eph-border) !important;
  border-radius: 18px !important;
  color: var(--eph-text) !important;
  background: radial-gradient(circle at 100% 0%, rgba(80, 104, 255, 0.11), transparent 38%), var(--eph-bg) !important;
  box-shadow: 0 20px 55px rgba(0, 0, 0, 0.36) !important;
}

.employee-planning-hover--light {
  --eph-bg: #ffffff;
  --eph-panel: #f6f8fb;
  --eph-panel-strong: #eef2f7;
  --eph-border: rgba(24, 37, 56, 0.1);
  --eph-text: #172033;
  --eph-muted: #6f7b8d;
  --eph-track: rgba(28, 43, 66, 0.1);
  box-shadow: 0 18px 48px rgba(30, 44, 70, 0.18) !important;
}

.employee-planning-hover__accent {
  position: absolute;
  inset: 0 0 auto;
  height: 3px;
  background: #ffb020;
}

.employee-planning-hover__accent.employee-planning-hover--balanced {
  background: #37c878;
}
.employee-planning-hover__accent.employee-planning-hover--neutral {
  background: #8b95a5;
}
.employee-planning-hover__accent.employee-planning-hover--near {
  background: #4f9cff;
}
.employee-planning-hover__accent.employee-planning-hover--over {
  background: #ff6478;
}

.employee-planning-hover__header,
.employee-planning-hover__hours,
.employee-planning-hover__metrics,
.employee-planning-hover__week {
  padding-right: 16px;
  padding-left: 16px;
}

.employee-planning-hover__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding-top: 16px;
  padding-bottom: 13px;
}

.employee-planning-hover__identity {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 11px;
}

.employee-planning-hover__avatar {
  flex: 0 0 auto;
  border: 2px solid rgba(79, 156, 255, 0.56);
  background: linear-gradient(135deg, #3b82f6, #6d5ee8) !important;
  box-shadow: 0 7px 18px rgba(48, 114, 232, 0.25);
}

.employee-planning-hover__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.employee-planning-hover__initials {
  color: #fff;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.employee-planning-hover__person-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.employee-planning-hover__eyebrow,
.employee-planning-hover__section-label {
  color: #70aefc;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.13em;
  line-height: 1.2;
  text-transform: uppercase;
}

.employee-planning-hover__name {
  overflow: hidden;
  margin-top: 3px;
  color: var(--eph-text);
  font-size: 17px;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.employee-planning-hover__contract {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  color: var(--eph-muted);
  font-size: 11px;
  font-weight: 600;
}

.employee-planning-hover__contract .v-icon {
  color: currentColor;
}

.employee-planning-hover__state {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 5px;
  margin-top: 2px;
  padding: 5px 8px;
  border: 1px solid rgba(255, 176, 32, 0.28);
  border-radius: 999px;
  color: #ffc453;
  background: rgba(255, 176, 32, 0.1);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.employee-planning-hover__state-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 0 3px rgba(255, 176, 32, 0.12);
}

.employee-planning-hover__state.employee-planning-hover--balanced {
  border-color: rgba(55, 200, 120, 0.3);
  color: #55db91;
  background: rgba(55, 200, 120, 0.1);
}

.employee-planning-hover__state.employee-planning-hover--neutral {
  border-color: rgba(139, 149, 165, 0.3);
  color: #aab2bf;
  background: rgba(139, 149, 165, 0.1);
}

.employee-planning-hover__state.employee-planning-hover--near {
  border-color: rgba(79, 156, 255, 0.3);
  color: #70aefc;
  background: rgba(79, 156, 255, 0.1);
}

.employee-planning-hover__state.employee-planning-hover--over {
  border-color: rgba(255, 100, 120, 0.3);
  color: #ff8190;
  background: rgba(255, 100, 120, 0.1);
}

.employee-planning-hover__hours {
  padding-top: 12px;
  padding-bottom: 12px;
  border-top: 1px solid var(--eph-border);
  border-bottom: 1px solid var(--eph-border);
  background: var(--eph-panel);
}

.employee-planning-hover__hours-main {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
}

.employee-planning-hover__hours-value {
  margin-top: 3px;
  color: var(--eph-text);
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.035em;
  line-height: 1;
}

.employee-planning-hover__hours-value small {
  color: var(--eph-muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
}

.employee-planning-hover__balance {
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  color: #ffbd44;
}

.employee-planning-hover__balance.employee-planning-hover--balanced {
  color: #48d487;
}
.employee-planning-hover__balance.employee-planning-hover--neutral {
  color: #aab2bf;
}
.employee-planning-hover__balance.employee-planning-hover--near {
  color: #61a8ff;
}
.employee-planning-hover__balance.employee-planning-hover--over {
  color: #ff7485;
}

.employee-planning-hover__balance span {
  font-size: 17px;
  font-weight: 800;
  line-height: 1;
}

.employee-planning-hover__balance small {
  margin-top: 3px;
  color: var(--eph-muted);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.employee-planning-hover__progress-track {
  position: relative;
  height: 7px;
  overflow: hidden;
  margin-top: 11px;
  border-radius: 99px;
  background: var(--eph-track);
}

.employee-planning-hover__progress-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #f89b2d, #ffc24f);
  box-shadow: 0 0 12px rgba(255, 176, 32, 0.28);
}

.employee-planning-hover__progress-fill.employee-planning-hover--balanced {
  background: linear-gradient(90deg, #23b968, #55db91);
}

.employee-planning-hover__progress-fill.employee-planning-hover--neutral {
  background: linear-gradient(90deg, #747f90, #aab2bf);
}

.employee-planning-hover__progress-fill.employee-planning-hover--near {
  background: linear-gradient(90deg, #347eea, #64adff);
}

.employee-planning-hover__progress-fill.employee-planning-hover--over {
  background: linear-gradient(90deg, #ef4662, #ff7d8c);
}

.employee-planning-hover__target-marker {
  position: absolute;
  top: -2px;
  right: 0;
  width: 2px;
  height: 11px;
  border-radius: 2px;
  background: var(--eph-text);
  opacity: 0.72;
}

.employee-planning-hover__progress-caption {
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
  color: var(--eph-muted);
  font-size: 9px;
  font-weight: 600;
}

.employee-planning-hover__metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px;
  padding-top: 11px;
  padding-bottom: 11px;
}

.employee-planning-hover__metric {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 8px;
  padding: 8px;
  border: 1px solid var(--eph-border);
  border-radius: 11px;
  background: var(--eph-panel);
}

.employee-planning-hover__metric-icon {
  display: inline-flex;
  width: 29px;
  height: 29px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
}

.employee-planning-hover__metric-icon .v-icon {
  color: currentColor;
}
.employee-planning-hover__metric-icon--blue {
  color: #70aefc;
  background: rgba(79, 156, 255, 0.13);
}
.employee-planning-hover__metric-icon--violet {
  color: #b596ff;
  background: rgba(142, 99, 246, 0.13);
}
.employee-planning-hover__metric-icon--amber {
  color: #ffc25d;
  background: rgba(255, 176, 32, 0.13);
}
.employee-planning-hover__metric-icon--green {
  color: #55db91;
  background: rgba(55, 200, 120, 0.13);
}

.employee-planning-hover__metric > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.employee-planning-hover__metric strong {
  overflow: hidden;
  color: var(--eph-text);
  font-size: 11px;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.employee-planning-hover__metric span:not(.employee-planning-hover__metric-icon) {
  overflow: hidden;
  margin-top: 2px;
  color: var(--eph-muted);
  font-size: 9px;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.employee-planning-hover__mix {
  display: flex;
  gap: 5px;
}

.employee-planning-hover__mix b {
  color: var(--eph-muted);
  font-size: 9px;
}

.employee-planning-hover__week {
  padding-top: 10px;
  padding-bottom: 14px;
  border-top: 1px solid var(--eph-border);
}

.employee-planning-hover__week-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.employee-planning-hover__week-title > span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--eph-text);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.employee-planning-hover__week-title .v-icon {
  color: #70aefc;
}

.employee-planning-hover__week-title small {
  color: var(--eph-muted);
  font-size: 9px;
  font-weight: 600;
}

.employee-planning-hover__days {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 5px;
}

.employee-planning-hover__day {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 62px;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  padding: 5px 2px 7px;
  border: 1px solid var(--eph-border);
  border-radius: 10px;
  color: var(--eph-muted);
  background: var(--eph-panel);
  transition: transform 140ms ease, border-color 140ms ease, background-color 140ms ease;
}

.employee-planning-hover__day:hover {
  z-index: 1;
  transform: translateY(-2px);
  border-color: rgba(112, 174, 252, 0.45);
  background: var(--eph-panel-strong);
}

.employee-planning-hover__day-label {
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.employee-planning-hover__day strong {
  margin-top: 1px;
  color: var(--eph-text);
  font-size: 14px;
  line-height: 1.1;
}

.employee-planning-hover__day-hours {
  overflow: hidden;
  width: 100%;
  margin-top: 3px;
  font-size: 8px;
  font-weight: 700;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.employee-planning-hover__day-dot {
  position: absolute;
  right: 5px;
  bottom: 4px;
  left: 5px;
  height: 2px;
  border-radius: 99px;
  background: rgba(156, 165, 180, 0.25);
}

.employee-planning-hover__day--planned {
  color: #59da93;
  background: rgba(55, 200, 120, 0.09);
}
.employee-planning-hover__day--planned .employee-planning-hover__day-dot {
  background: #37c878;
}
.employee-planning-hover__day--external {
  color: #44cee3;
  background: rgba(37, 192, 211, 0.09);
}
.employee-planning-hover__day--external .employee-planning-hover__day-dot {
  background: #25c0d3;
}
.employee-planning-hover__day--vacation {
  color: #67abff;
  background: rgba(79, 156, 255, 0.09);
}
.employee-planning-hover__day--vacation .employee-planning-hover__day-dot {
  background: #4f9cff;
}
.employee-planning-hover__day--school {
  color: #bb98ff;
  background: rgba(142, 99, 246, 0.09);
}
.employee-planning-hover__day--school .employee-planning-hover__day-dot {
  background: #8e63f6;
}
.employee-planning-hover__day--wish {
  color: #ff84bd;
  background: rgba(238, 81, 157, 0.09);
}
.employee-planning-hover__day--wish .employee-planning-hover__day-dot {
  background: #ee519d;
}
.employee-planning-hover__day--absence {
  color: #ff7f8e;
  background: rgba(255, 100, 120, 0.09);
}
.employee-planning-hover__day--absence .employee-planning-hover__day-dot {
  background: #ff6478;
}
.employee-planning-hover__day--conflict {
  color: #ffc25d;
  background: rgba(255, 176, 32, 0.1);
}
.employee-planning-hover__day--conflict .employee-planning-hover__day-dot {
  background: #ffb020;
}
.employee-planning-hover__day--free {
  color: #98a2b2;
}

.employee-planning-hover__tooltip {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px 1px;
  font-size: 11px;
}

.employee-planning-hover__tooltip strong {
  font-size: 12px;
}
.employee-planning-hover__tooltip span {
  opacity: 0.88;
}

@media (max-width: 450px) {
  .employee-planning-hover__state {
    display: none;
  }
  .employee-planning-hover__metrics {
    grid-template-columns: 1fr;
  }
}
</style>
